/**
 * Utility functions for reading cookies client-side.
 * Used to pass data from Next.js middleware to client-side code.
 */

import * as Sentry from '@sentry/nextjs';

/**
 * Gets a cookie value by name.
 * Returns null if the cookie doesn't exist.
 */
export function getCookie(name: string): string | null {
	if (typeof document === 'undefined') {
		return null;
	}

	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${name}=`);

	if (parts.length === 2) {
		const cookieValue = parts.pop()?.split(';').shift();
		if (!cookieValue) return null;
		try {
			return decodeURIComponent(cookieValue);
		} catch (error) {
			// Cookie value may contain raw % or malformed encoding; return as-is
			Sentry.withScope((scope) => {
				scope.setTag('module', 'cookies');
				scope.setTag('errorType', 'decode');
				scope.setLevel('warning');
				scope.setContext('cookie', { name, valueLength: cookieValue.length });
				Sentry.captureException(error);
			});
			return cookieValue;
		}
	}

	return null;
}

/**
 * Parses a JSON cookie value.
 * Returns null if parsing fails or cookie doesn't exist.
 */
export function getJSONCookie<T>(name: string): T | null {
	const cookieValue = getCookie(name);
	if (!cookieValue) {
		return null;
	}

	try {
		return JSON.parse(cookieValue) as T;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.warn(`Failed to parse cookie "${name}":`, error);
		Sentry.withScope((scope) => {
			scope.setTag('module', 'cookies');
			scope.setTag('errorType', 'json-parse');
			scope.setLevel('warning');
			scope.setContext('cookie', {
				name,
				valueLength: cookieValue.length,
				valuePreview: cookieValue.slice(0, 100),
			});
			Sentry.captureException(error);
		});
		return null;
	}
}
